import React, {
    useState,
    useEffect
}
from "react";

import {
    FontAwesomeIcon
}
from "@fortawesome/react-fontawesome";

import {
    faFilePdf,
    faFile,
    faFolder,
    faChevronRight
}
from "@fortawesome/free-solid-svg-icons";

import useFolderForagerBuilding
from "../../hooks/useFolderForagerBuilding";

import {
    functions
}
from "../../../../constants/common-functions";

import MobileDocumentViewer
from "./MobileDocumentViewer";


//============================================================
// Mobile Facility Documents
//============================================================

export default function MobileFacilityDocuments(
{
    facilityId,
    facilityCode
})
{

    //--------------------------------------------------------
    // Data
    //--------------------------------------------------------

    const {
        loading = false,
        directories = [],
        selectedDirectory = null,
        documents = [],
        selectedDocument = null,
        setSelectedDocument,
        expandDirectory,
        selectDirectory
    } =
        useFolderForagerBuilding(
            facilityId,
            facilityCode
        ) || {};


    //--------------------------------------------------------
    // Document Viewer
    //--------------------------------------------------------

    const [openDocumentUrl, setOpenDocumentUrl] =
        useState(null);


    //========================================================
    // Document Events
    //========================================================

    const openFile =
        function(document)
        {
            //------------------------------------------------
            // Get actual document from SharePoint
            //------------------------------------------------

            functions.getBlobFromAjax(
                "/resolutionsoftwareplatform/api/sharepoint/file/" +
                document.sharePointId,

                function(blob)
                {
                    //------------------------------------------------
                    // Remove previous blob URL if required
                    //------------------------------------------------

                    if(openDocumentUrl)
                    {
                        window.URL.revokeObjectURL(
                            openDocumentUrl
                        );
                    }


                    //------------------------------------------------
                    // Create local browser URL
                    //------------------------------------------------

                    const url =
                        window.URL.createObjectURL(
                            blob
                        );


                    //------------------------------------------------
                    // Open viewer
                    //------------------------------------------------

                    setSelectedDocument(
                        document
                    );

                    setOpenDocumentUrl(
                        url
                    );
                }
            );
        };


    //--------------------------------------------------------
    // Close document
    //--------------------------------------------------------

    const closeFile =
        function()
        {
            //------------------------------------------------
            // Release browser blob
            //------------------------------------------------

            if(openDocumentUrl)
            {
                window.URL.revokeObjectURL(
                    openDocumentUrl
                );
            }


            //------------------------------------------------
            // Close viewer
            //------------------------------------------------

            setOpenDocumentUrl(
                null
            );

            setSelectedDocument(
                null
            );
        };


    //--------------------------------------------------------
    // Cleanup
    //--------------------------------------------------------

    useEffect(
        function()
        {
            return function()
            {
                if(openDocumentUrl)
                {
                    window.URL.revokeObjectURL(
                        openDocumentUrl
                    );
                }
            };
        },
        [openDocumentUrl]
    );


    //--------------------------------------------------------
    // Document icon
    //--------------------------------------------------------

    const getDocumentIcon =
        function(extension)
        {
            if(
                extension &&
                extension.toLowerCase() === "pdf"
            )
            {
                return faFilePdf;
            }


            return faFile;
        };


    //========================================================
    // Directory Events
    //========================================================

    const handleDirectoryClick =
        function(directory)
        {
            //------------------------------------------------
            // Select directory and load documents
            //------------------------------------------------

            selectDirectory(
                directory
            );


            //------------------------------------------------
            // Load child directories first time
            //------------------------------------------------

            if(
                !directory.loaded
            )
            {
                expandDirectory(
                    directory
                );
            }
        };


    //--------------------------------------------------------
    // Child directory
    //--------------------------------------------------------

    const handleChildDirectoryClick =
        function(directory)
        {
            selectDirectory(
                directory
            );


            //------------------------------------------------
            // Allows deeper directory structures later
            //------------------------------------------------

            if(
                !directory.loaded
            )
            {
                expandDirectory(
                    directory
                );
            }
        };


    //========================================================
    // Styles
    //========================================================

    const container =
    {
        width:
            "calc(100% - 20px)",

        margin:
            "12px 10px",

        boxSizing:
            "border-box",

        background:
            "rgba(255,255,255,0.82)",

        border:
            "1px solid rgba(255,255,255,0.75)",

        borderRadius:
            "14px",

        boxShadow:
            "0 6px 18px rgba(0,0,0,0.10)",

        overflow:
            "hidden"
    };


    //--------------------------------------------------------
    // Header
    //--------------------------------------------------------

    const header =
    {
        display:
            "flex",

        alignItems:
            "center",

        height:
            "48px",

        padding:
            "0 14px",

        gap:
            "10px",

        background:
            "linear-gradient(180deg,#2f3339 0%,#262a2f 100%)",

        color:
            "white"
    };


    const headerIcon =
    {
        display:
            "flex",

        width:
            "30px",

        height:
            "30px",

        alignItems:
            "center",

        justifyContent:
            "center",

        borderRadius:
            "50%",

        background:
            "rgba(255,255,255,0.12)",

        fontSize:
            "12px"
    };


    const headerTitle =
    {
        flexGrow:
            1,

        fontSize:
            "13px",

        fontWeight:
            700
    };


    const headerCount =
    {
        fontSize:
            "11px",

        opacity:
            0.7
    };


    //========================================================
    // Directories
    //========================================================

    const directoryScroller =
    {
        display:
            "flex",

        gap:
            "8px",

        width:
            "100%",

        padding:
            "12px",

        boxSizing:
            "border-box",

        overflowX:
            "auto",

        WebkitOverflowScrolling:
            "touch",

        borderBottom:
            "1px solid rgba(38,49,61,0.08)"
    };


    //--------------------------------------------------------
    // Directory pill
    //--------------------------------------------------------

    const getDirectoryStyle =
        function(selected)
        {
            return {
                display:
                    "flex",

                alignItems:
                    "center",

                gap:
                    "7px",

                flexShrink:
                    0,

                height:
                    "34px",

                padding:
                    "0 12px",

                borderRadius:
                    "17px",

                background:
                    selected
                        ?
                        "linear-gradient(180deg,#5A9BD6 0%,#356EA1 100%)"
                        :
                        "#f0f2f4",

                color:
                    selected
                        ?
                        "#ffffff"
                        :
                        "#4b5563",

                fontSize:
                    "11px",

                fontWeight:
                    600,

                cursor:
                    "pointer",

                boxShadow:
                    selected
                        ?
                        "0 3px 8px rgba(53,110,161,0.25)"
                        :
                        "none",

                transition:
                    "all .18s ease"
            };
        };


    //--------------------------------------------------------
    // Child directory pill
    //--------------------------------------------------------

    const getChildDirectoryStyle =
        function(selected)
        {
            return {
                ...getDirectoryStyle(
                    selected
                ),

                background:
                    selected
                        ?
                        "linear-gradient(180deg,#5A9BD6 0%,#356EA1 100%)"
                        :
                        "#ffffff",

                border:
                    selected
                        ?
                        "1px solid transparent"
                        :
                        "1px solid rgba(38,49,61,0.12)"
            };
        };


    //========================================================
    // Files
    //========================================================

    const fileRow =
    {
        display:
            "flex",

        alignItems:
            "center",

        minHeight:
            "58px",

        padding:
            "8px 14px",

        gap:
            "12px",

        boxSizing:
            "border-box",

        borderBottom:
            "1px solid rgba(38,49,61,0.07)",

        cursor:
            "pointer"
    };


    const fileIcon =
    {
        display:
            "flex",

        width:
            "36px",

        height:
            "36px",

        flexShrink:
            0,

        alignItems:
            "center",

        justifyContent:
            "center",

        borderRadius:
            "9px",

        background:
            "#edf5fc",

        color:
            "#3d7bff",

        fontSize:
            "15px"
    };


    const fileInfo =
    {
        flexGrow:
            1,

        minWidth:
            0
    };


    const fileTitle =
    {
        color:
            "#26313D",

        fontSize:
            "12px",

        fontWeight:
            600,

        overflow:
            "hidden",

        textOverflow:
            "ellipsis",

        whiteSpace:
            "nowrap"
    };


    const fileMeta =
    {
        marginTop:
            "3px",

        color:
            "#8A939D",

        fontSize:
            "10px"
    };


    const chevron =
    {
        color:
            "#A7AFB8",

        fontSize:
            "11px"
    };


    //--------------------------------------------------------
    // Empty
    //--------------------------------------------------------

    const empty =
    {
        padding:
            "22px",

        textAlign:
            "center",

        color:
            "#8A939D",

        fontSize:
            "12px"
    };


    //========================================================
    // Render Directories
    //========================================================

    const renderDirectories =
        function()
        {
            if(
                directories.length === 0
            )
            {
                return null;
            }


            return (
                <div style={directoryScroller}>
                    {
                        directories.map(
                            function(item)
                            {
                                const selected =
                                    selectedDirectory &&
                                    item.id ===
                                    selectedDirectory.id;


                                return (
                                    <React.Fragment
                                        key={item.id}
                                    >

                                        {/* ROOT DIRECTORY */}

                                        <div
                                            style={
                                                getDirectoryStyle(
                                                    selected
                                                )
                                            }
                                            onClick={
                                                function()
                                                {
                                                    handleDirectoryClick(
                                                        item
                                                    );
                                                }
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faFolder}
                                            />

                                            {item.name}

                                        </div>


                                        {/* CHILD DIRECTORIES */}

                                        {
                                            item.expanded
                                            &&
                                            item.children
                                            &&
                                            item.children.map(
                                                function(child)
                                                {
                                                    const childSelected =
                                                        selectedDirectory &&
                                                        child.id ===
                                                        selectedDirectory.id;


                                                    return (
                                                        <div
                                                            key={child.id}
                                                            style={
                                                                getChildDirectoryStyle(
                                                                    childSelected
                                                                )
                                                            }
                                                            onClick={
                                                                function()
                                                                {
                                                                    handleChildDirectoryClick(
                                                                        child
                                                                    );
                                                                }
                                                            }
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faFolder}
                                                            />

                                                            {child.name}

                                                        </div>
                                                    );
                                                }
                                            )
                                        }

                                    </React.Fragment>
                                );
                            }
                        )
                    }
                </div>
            );
        };


    //========================================================
    // Render Documents
    //========================================================

    const renderDocuments =
        function()
        {
            //------------------------------------------------
            // Loading
            //------------------------------------------------

            if(
                loading
            )
            {
                return (
                    <div style={empty}>
                        Loading documents...
                    </div>
                );
            }


            //------------------------------------------------
            // Nothing selected
            //------------------------------------------------

            if(
                !selectedDirectory
            )
            {
                return (
                    <div style={empty}>
                        Select a folder to view documents
                    </div>
                );
            }


            //------------------------------------------------
            // No documents
            //------------------------------------------------

            if(
                documents.length === 0
            )
            {
                return (
                    <div style={empty}>
                        No documents in this folder
                    </div>
                );
            }


            //------------------------------------------------
            // Documents
            //------------------------------------------------

            return documents.map(
                function(file)
                {
                    return (
                        <div
                            key={file.id}
                            style={fileRow}
                            onClick={
                                function()
                                {
                                    openFile(
                                        file
                                    );
                                }
                            }
                        >

                            {/* ICON */}

                            <div style={fileIcon}>
                                <FontAwesomeIcon
                                    icon={
                                        getDocumentIcon(
                                            file.extension
                                        )
                                    }
                                />
                            </div>


                            {/* DETAILS */}

                            <div style={fileInfo}>

                                <div style={fileTitle}>
                                    {file.title}
                                </div>


                                <div style={fileMeta}>

                                    {
                                        file.extension
                                            ?.toUpperCase()
                                    }

                                    {
                                        file.documentDate
                                        &&
                                        <>
                                            {" • "}

                                            {
                                                functions.formatDate(
                                                    file.documentDate
                                                )
                                            }

                                        </>
                                    }

                                </div>

                            </div>


                            {/* OPEN */}

                            <FontAwesomeIcon
                                icon={faChevronRight}
                                style={chevron}
                            />

                        </div>
                    );
                }
            );
        };


    //========================================================
    // Render
    //========================================================

    return (
        <>

            <div style={container}>

                {/* ==============================================
                    HEADER
                ============================================== */}

                <div style={header}>

                    <div style={headerIcon}>
                        <FontAwesomeIcon
                            icon={faFolder}
                        />
                    </div>


                    <div style={headerTitle}>
                        Documents & Resources
                    </div>


                    <div style={headerCount}>
                        {documents.length} files
                    </div>

                </div>


                {/* ==============================================
                    DIRECTORIES
                ============================================== */}

                {
                    renderDirectories()
                }


                {/* ==============================================
                    DOCUMENTS
                ============================================== */}

                {
                    renderDocuments()
                }

            </div>


            {/* ==============================================
                DOCUMENT VIEWER
            ============================================== */}

            {
                openDocumentUrl
                &&
                <MobileDocumentViewer
                    title={
                        selectedDocument
                        ?
                        selectedDocument.title
                        :
                        "Document"
                    }
                    url={openDocumentUrl}
                    closeFunction={closeFile}
                />
            }

        </>
    );
}